import React, { useEffect, useState } from "react";
import { Differ, Viewer } from "json-diff-kit";
import "json-diff-kit/dist/viewer.css";


interface JsonDiffViewerProps {
  initialJson: Record<string, any>;
  updatedJson: Record<string, any>;
  onConfirm: () => void;
  onClose: () => void;
}

const JsonDiffViewer: React.FC<JsonDiffViewerProps> = ({
  initialJson,
  updatedJson,
  onConfirm,
  onClose,
}) => {
  const [diff, setDiff] = useState<any | null>(null);

  useEffect(() => {
    const d = new Differ({
      detectCircular: true,
      showModifications: true,
      arrayDiffMethod: "lcs",
      ignoreCase: false,
      ignoreCaseForKey: false,
      recursiveEqual: false,
    });

    const diffResult = d.diff(initialJson, updatedJson);
    setDiff(diffResult);
  }, [initialJson, updatedJson]);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === "Escape" || ev.key === "Esc") {
        onClose();
      } 
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, onConfirm]);

  const viewerProps = {
    indent: 2,
    lineNumbers: true,
    highlightInlineDiff: true,
    inlineDiffOptions: {
      mode: "char" as "char",
      wordSeparator: " ",
    },
    hideUnchangedLines: false, // show full side-by-side
    virtual: false,
    sideBySide: true, // <-- important: side-by-side columns
    splitView: true,
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 9999,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "10px",
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          width: "90vw",
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #eee",
            fontWeight: 600,
            fontSize: "18px",
          }}
        >
          JSON Differences
        </div>

        {/* Diff Content */}
        <div
          style={{
            flex: 1,
            padding: "16px 24px",
            overflow: "auto",
            backgroundColor: "#f5f5f5",
            fontFamily: "monospace",
            fontSize: "13px",
          }}
        >
          {diff ? <Viewer diff={diff} {...viewerProps} /> : <div>Loading diff...</div>}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 24px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            backgroundColor: "#fafafa",
            borderTop: "1px solid #eee",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              backgroundColor: "#fff",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              backgroundColor: "#145870",
              color: "#fff",
              border: "none",
              cursor: "pointer",
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default JsonDiffViewer;
